import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { TOASTS } from "@/lib/toasts";
import { logError } from "@/lib/errorHandler";

export interface CartItemMeta {
  event_date?: string;
  event_time?: string;
  location?: string;
  size?: string;
  color?: string;
  spots_left?: number;
}

export interface CartItem {
  id: string;
  item_type: "event" | "product";
  event_id?: string;
  product_id?: string;
  title: string;
  unit_price: number;
  quantity: number;
  image_url?: string;
  meta?: CartItemMeta;
}

export interface EventForCart {
  id: string;
  title: string;
  price: number;
  event_date: string;
  event_time: string;
  location: string;
  max_spots: number;
  current_spots: number;
}

export interface ProductForCart {
  id: string;
  name: string;
  price: number;
  stock: number;
  image_url?: string | null;
}

interface CartContextValue {
  items: CartItem[];
  eventItems: CartItem[];
  productItems: CartItem[];
  itemCount: number;
  total: number;
  isOpen: boolean;
  loading: boolean;
  addEvent: (event: EventForCart) => Promise<boolean>;
  addProduct: (product: ProductForCart, meta?: Partial<CartItemMeta>) => Promise<boolean>;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  clearCart: () => Promise<void>;
  openCart: () => void;
  closeCart: () => void;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Computed values
  const eventItems = items.filter((item) => item.item_type === "event");
  const productItems = items.filter((item) => item.item_type === "product");
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const total = items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);

  // Fetch cart items from Supabase
  const fetchCart = useCallback(async () => {
    if (!user) {
      setItems([]);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("cart_items")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setItems(
        (data || []).map((item) => ({
          id: item.id,
          item_type: item.item_type as "event" | "product",
          event_id: item.event_id ?? undefined,
          product_id: item.product_id ?? undefined,
          title: item.title,
          unit_price: Number(item.unit_price),
          quantity: item.quantity,
          image_url: item.image_url ?? undefined,
          meta: (item.meta as CartItemMeta) ?? undefined,
        }))
      );
    } catch (error) {
      logError("fetch-cart", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch cart on user change
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Add event to cart
  const addEvent = async (event: EventForCart): Promise<boolean> => {
    if (!user) {
      toast.error(TOASTS.error.not_logged_in);
      return false;
    }

    // Check if event already in cart
    if (items.some((item) => item.event_id === event.id)) {
      toast.info("Ya tenés este evento en tu reserva");
      return false;
    }

    try {
      const { error } = await supabase.from("cart_items").insert({
        user_id: user.id,
        item_type: "event",
        event_id: event.id,
        title: event.title,
        unit_price: event.price,
        quantity: 1,
        meta: {
          event_date: event.event_date,
          event_time: event.event_time,
          location: event.location,
          spots_left: event.max_spots - event.current_spots,
        },
      });

      if (error) throw error;

      await fetchCart();
      toast.success(TOASTS.success.event_reserved);
      return true;
    } catch (error) {
      logError("add-event-to-cart", error);
      toast.error(TOASTS.error.generic);
      return false;
    }
  };

  // Add product to cart
  const addProduct = async (
    product: ProductForCart,
    meta?: Partial<CartItemMeta>
  ): Promise<boolean> => {
    if (!user) {
      toast.error(TOASTS.error.not_logged_in);
      return false;
    }

    // Check if product already in cart
    const existingItem = items.find((item) => item.product_id === product.id);

    if (existingItem) {
      // Update quantity
      if (existingItem.quantity >= product.stock) {
        toast.error(TOASTS.error.no_stock);
        return false;
      }
      await updateQuantity(existingItem.id, existingItem.quantity + 1);
      toast.success(TOASTS.success.product_added);
      return true;
    }

    try {
      const { error } = await supabase.from("cart_items").insert({
        user_id: user.id,
        item_type: "product",
        product_id: product.id,
        title: product.name,
        unit_price: product.price,
        quantity: 1,
        image_url: product.image_url,
        meta: meta || {},
      });

      if (error) throw error;

      await fetchCart();
      toast.success(TOASTS.success.product_added);
      return true;
    } catch (error) {
      logError("add-product-to-cart", error);
      toast.error(TOASTS.error.generic);
      return false;
    }
  };

  // Update item quantity
  const updateQuantity = async (id: string, quantity: number) => {
    if (quantity <= 0) {
      await removeItem(id);
      return;
    }

    // Stock validation: if this item is a product, refetch the live stock
    // before persisting. Prevents users from arbitrarily increasing quantity
    // beyond what's in inventory by manipulating the client.
    const targetItem = items.find((it) => it.id === id);
    if (targetItem?.item_type === "product" && targetItem.product_id) {
      try {
        const { data: product, error: stockError } = await supabase
          .from("products")
          .select("stock")
          .eq("id", targetItem.product_id)
          .maybeSingle();

        if (stockError) throw stockError;

        const stock = product?.stock ?? 0;
        if (quantity > stock) {
          toast.error(
            `Stock insuficiente: solo ${stock === 0 ? "no quedan unidades" : `quedan ${stock} ${stock === 1 ? "unidad" : "unidades"}`}`
          );
          return;
        }
      } catch (error) {
        logError("validate-stock-on-update", error);
        toast.error(TOASTS.error.generic);
        return;
      }
    }

    try {
      const { error } = await supabase
        .from("cart_items")
        .update({ quantity })
        .eq("id", id);

      if (error) throw error;

      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, quantity } : item))
      );
    } catch (error) {
      logError("update-cart-quantity", error);
      toast.error(TOASTS.error.generic);
    }
  };

  // Remove item from cart
  const removeItem = async (id: string) => {
    try {
      const { error } = await supabase.from("cart_items").delete().eq("id", id);

      if (error) throw error;

      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      logError("remove-cart-item", error);
      toast.error(TOASTS.error.generic);
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("user_id", user.id);

      if (error) throw error;

      setItems([]);
    } catch (error) {
      logError("clear-cart", error);
    }
  };

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  return (
    <CartContext.Provider
      value={{
        items,
        eventItems,
        productItems,
        itemCount,
        total,
        isOpen,
        loading,
        addEvent,
        addProduct,
        updateQuantity,
        removeItem,
        clearCart,
        openCart,
        closeCart,
        refreshCart: fetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
